import React, { useEffect, useState } from 'react';
import { PricingCard } from './PricingCard';
import { InlineLoader } from '../ui/page-loader';
import {
  CURRENCY_OPTIONS,
  CURRENCY_SYMBOLS,
  FALLBACK_PLANS,
  fetchSubscriptionPlans,
} from '../../lib/pricing';

export function PricingGrid({
  api,
  currency: controlledCurrency,
  onCurrencyChange,
  showCurrencySelector = true,
  onSelectPlan,
  ctaHref,
  currentPlanId,
  preselectedPlanId,
  processingPlanId,
  className = '',
  gridClassName = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-stretch overflow-visible pt-5',
}) {
  const isAuthenticatedContext = Boolean(api);
  const [plans, setPlans] = useState(isAuthenticatedContext ? [] : FALLBACK_PLANS);
  const [loading, setLoading] = useState(isAuthenticatedContext);
  const [internalCurrency, setInternalCurrency] = useState('AUD');
  const currency = controlledCurrency ?? internalCurrency;

  useEffect(() => {
    let cancelled = false;

    async function loadPlans() {
      if (isAuthenticatedContext) setLoading(true);
      try {
        const data = await fetchSubscriptionPlans({ api, currency });
        if (!cancelled && Array.isArray(data) && data.length) {
          setPlans(data);
        }
      } catch (error) {
        console.error('Failed to fetch plans:', error);
        if (!cancelled && !isAuthenticatedContext) {
          setPlans(FALLBACK_PLANS);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPlans();
    return () => {
      cancelled = true;
    };
  }, [api, currency, isAuthenticatedContext]);

  const handleCurrencyChange = (nextCurrency) => {
    if (onCurrencyChange) {
      onCurrencyChange(nextCurrency);
    } else {
      setInternalCurrency(nextCurrency);
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-16 ${className}`}>
        <InlineLoader message="Loading plans" />
      </div>
    );
  }

  return (
    <div className={className}>
      {showCurrencySelector && (
        <div className="flex justify-center gap-2 mb-8 flex-wrap">
          {CURRENCY_OPTIONS.map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => handleCurrencyChange(code)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                currency === code
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-border hover:border-primary hover:text-primary'
              }`}
              data-testid={`currency-${code}`}
            >
              {CURRENCY_SYMBOLS[code]} {code}
            </button>
          ))}
        </div>
      )}

      <div className={gridClassName}>
        {plans.map((plan) => (
          <PricingCard
            key={plan.id}
            plan={plan}
            currency={currency}
            onSelectPlan={onSelectPlan}
            ctaHref={onSelectPlan ? undefined : ctaHref}
            ctaDisabled={plan.id === currentPlanId}
            ctaLoading={processingPlanId === plan.id}
            isCurrent={plan.id === currentPlanId}
            isPreselected={plan.id === preselectedPlanId}
          />
        ))}
      </div>
    </div>
  );
}
